<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import nexceoProjectOptionsAPI from 'dashboard/api/nexceo/projectOptions';
import nexceoKanbanStagesAPI from 'dashboard/api/nexceo/kanbanStages';
import KanbanSummaryCard from './KanbanSummaryCard.vue';
import KanbanColumn from './KanbanColumn.vue';
import LeadDetailsDrawer from './LeadDetailsDrawer.vue';
import LeadForm from './LeadForm.vue';
import TodaySiteVisitsWidget from './TodaySiteVisitsWidget.vue';

const store = useStore();
const { accountId } = useAccount();
const router = useRouter();

const projects = ref([]);
const stages = ref([]);
const agents = computed(() => store.getters['agents/getAgents']);
const leads = computed(() => store.getters['nexceoLeads/allLeads']);
const uiFlags = computed(() => store.getters['nexceoLeads/getUIFlags']);

const selectedProjectId = ref('');
const selectedAgentId = ref('');
const selectedChannel = ref('');
const selectedPriority = ref('');
const selectedLeadType = ref('');
const searchQuery = ref('');
const showFilters = ref(false);

const selectedLead = ref(null);
const isDrawerOpen = ref(false);
const isLeadFormOpen = ref(false);
const selectedStageId = ref(null);
const loadError = ref('');
const todayVisitsWidgetRef = ref(null);

// Backend deduplicates stages for the unified view and filters by project when
// project_id is passed, so no client-side stage filtering is needed here.
const filteredStages = computed(() => stages.value);

const filteredLeads = computed(() => {
  let list = leads.value;
  if (selectedProjectId.value)
    list = list.filter(
      l => String(l.project_id) === String(selectedProjectId.value)
    );
  if (selectedAgentId.value)
    list = list.filter(
      l => String(l.assigned_agent_id) === String(selectedAgentId.value)
    );
  if (selectedChannel.value)
    list = list.filter(l => l.source_channel === selectedChannel.value);
  if (selectedPriority.value)
    list = list.filter(l => l.priority === selectedPriority.value);
  if (selectedLeadType.value === 'broker')
    list = list.filter(l => l.lead_type === 'broker' || l.broker_detected);
  else if (selectedLeadType.value === 'buyer')
    list = list.filter(l => l.lead_type !== 'broker' && !l.broker_detected);
  if (searchQuery.value.trim()) {
    const q = searchQuery.value.trim().toLowerCase();
    list = list.filter(
      l =>
        (l.name || '').toLowerCase().includes(q) ||
        (l.phone_number || '').includes(q) ||
        (l.email || '').toLowerCase().includes(q)
    );
  }
  return list;
});

// Match by canonical stage key so leads from all projects appear in the correct
// unified column, regardless of which project's stage ID the lead holds.
function leadsForStage(stageKey) {
  return filteredLeads.value.filter(l => l.kanban_stage?.key === stageKey);
}

const SUMMARY_STAGES = [
  {
    key: 'new_enquiry',
    label: 'New Enquiry',
    icon: 'i-lucide-mail-open',
    color: '#6366f1',
  },
  {
    key: 'qualified',
    label: 'Qualified',
    icon: 'i-lucide-star',
    color: '#f97316',
  },
  {
    key: 'site_visit_scheduled',
    label: 'Site Visit',
    icon: 'i-lucide-building-2',
    color: '#10b981',
  },
  {
    key: 'negotiation',
    label: 'Negotiation',
    icon: 'i-lucide-handshake',
    color: '#f59e0b',
  },
  {
    key: 'booked',
    label: 'Booked',
    icon: 'i-lucide-calendar-check',
    color: '#3b82f6',
  },
  { key: 'lost', label: 'Lost', icon: 'i-lucide-x-circle', color: '#ef4444' },
];

const summaryStats = computed(() => {
  const todayStart = new Date();
  todayStart.setHours(0, 0, 0, 0);
  return SUMMARY_STAGES.map(cfg => {
    // Match by key so the unified view aggregates leads from all projects.
    const stageLeads = filteredLeads.value.filter(
      l => l.kanban_stage?.key === cfg.key
    );
    const todayCount = stageLeads.filter(
      l => l.created_at && new Date(l.created_at) >= todayStart
    ).length;
    return { ...cfg, count: stageLeads.length, todayCount };
  });
});

const activeFilterCount = computed(
  () =>
    [
      selectedAgentId.value,
      selectedChannel.value,
      selectedPriority.value,
      selectedLeadType.value,
    ].filter(Boolean).length
);

async function loadStages() {
  const params = selectedProjectId.value
    ? { project_id: selectedProjectId.value }
    : {};
  const stagesRes = await nexceoKanbanStagesAPI.getAll(params);
  stages.value = Array.isArray(stagesRes.data)
    ? stagesRes.data
    : (stagesRes.data?.stages ?? []);
}

async function loadData() {
  loadError.value = '';
  try {
    const projectsRes = await nexceoProjectOptionsAPI.get();
    projects.value = projectsRes.data?.projects ?? [];
    await loadStages();
  } catch {
    loadError.value = 'Failed to load data. Please retry.';
  }
}

async function loadLeads() {
  const params = {};
  if (selectedProjectId.value) params.project_id = selectedProjectId.value;
  await store.dispatch('nexceoLeads/fetchLeads', params).catch(() => {
    loadError.value = 'Failed to load leads.';
  });
}

// Reload both stages (dedup/filter changes) and leads when project filter changes.
watch(selectedProjectId, () => Promise.all([loadStages(), loadLeads()]));

onMounted(async () => {
  await Promise.all([loadData(), loadLeads(), store.dispatch('agents/get')]);
});

function openDrawer(lead) {
  selectedLead.value = lead;
  isDrawerOpen.value = true;
}
function closeDrawer() {
  isDrawerOpen.value = false;
  selectedLead.value = null;
}

// Reschedule-audit fix: `selectedLead` was a one-time snapshot taken at
// openDrawer() time — a store-driven update to this same lead while the
// drawer stayed open (e.g. another agent/tab rescheduling its site visit, or
// the websocket-driven refreshLead patching the store) never reached the
// visible drawer until it was closed and reopened, or until the drawer's own
// action triggered onDrawerUpdated. Watching the store record directly keeps
// it live for every external update too, not just this drawer's own actions.
watch(
  () =>
    selectedLead.value &&
    store.getters['nexceoLeads/getLeadById'](selectedLead.value.id),
  updated => {
    if (updated) selectedLead.value = updated;
  }
);
function openLeadForm(stage = null) {
  const query = { return_to: 'kanban' };
  if (stage?.id) query.stage_id = stage.id;
  router.push({
    name: 'nexceo_leads_new',
    params: { accountId: accountId.value },
    query,
  });
}

function closeLeadForm() {
  isLeadFormOpen.value = false;
  selectedStageId.value = null;
}

async function onFormSaved() {
  closeLeadForm();
  await loadLeads();
  // Warn if active filters might be hiding the newly created lead
  const hasActiveFilters =
    selectedAgentId.value ||
    selectedChannel.value ||
    selectedPriority.value ||
    selectedLeadType.value;
  if (hasActiveFilters) {
    useAlert(
      'Lead created. It may be hidden by active filters — clear filters to see it.'
    );
  }
}
async function onMoveStage({ leadId, stageKey }) {
  try {
    await store.dispatch('nexceoLeads/moveStage', { leadId, stageKey });
  } catch {
    loadError.value = 'Failed to move stage.';
    // Rollback: resync columns from server so dragged card returns to its original position
    await loadLeads();
  }
}
async function onDrawerUpdated() {
  await loadLeads();
  // Re-sync the open drawer's lead from the freshly loaded store data
  if (selectedLead.value) {
    const refreshed = store.getters['nexceoLeads/getLeadById'](
      selectedLead.value.id
    );
    if (refreshed) selectedLead.value = refreshed;
  }
  // Refresh today's site visits count in the header widget
  todayVisitsWidgetRef.value?.load();
}
function clearFilters() {
  selectedAgentId.value = '';
  selectedChannel.value = '';
  selectedPriority.value = '';
  selectedLeadType.value = '';
}

const channelOptions = [
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'email', label: 'Email' },
  { value: 'facebook', label: 'Facebook' },
  { value: 'instagram', label: 'Instagram' },
  { value: 'web_widget', label: 'Website' },
  { value: 'website_contact_form', label: 'Contact Form' },
  { value: 'website_popup', label: 'Popup Form' },
  { value: 'api', label: 'API / Manual' },
];
const priorityOptions = [
  { value: 'urgent', label: 'Urgent' },
  { value: 'high', label: 'High' },
  { value: 'normal', label: 'Normal' },
  { value: 'low', label: 'Low' },
];
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <div class="flex flex-col w-full h-full overflow-hidden bg-n-background">
    <!-- Page Header -->
    <div
      class="flex items-center gap-3 px-4 h-14 bg-n-background border-b border-n-weak shrink-0"
    >
      <div class="flex items-center gap-2 flex-1 min-w-0">
        <span class="i-lucide-kanban size-4 text-woot-500 shrink-0" />
        <h1 class="text-base font-semibold text-n-slate-12 truncate">
          CRM Kanban
        </h1>

        <!-- Project filter -->
        <div class="relative ml-4 shrink-0">
          <select
            v-model="selectedProjectId"
            class="appearance-none text-sm border border-n-weak rounded-lg pl-3 pr-7 py-1.5 bg-n-background text-n-slate-11 outline-none focus:border-woot-500 cursor-pointer"
          >
            <option value="">All Projects</option>
            <option v-for="p in projects" :key="p.id" :value="p.id">
              {{ p.name }}
            </option>
          </select>
          <span
            class="i-lucide-chevron-down size-3.5 text-n-slate-8 absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none"
          />
        </div>
      </div>

      <!-- Right actions -->
      <div class="flex items-center gap-2 shrink-0">
        <!-- Search -->
        <div class="relative hidden sm:block">
          <span
            class="absolute left-2.5 top-1/2 -translate-y-1/2 i-lucide-search size-3.5 text-n-slate-7 pointer-events-none"
          />
          <input
            v-model="searchQuery"
            type="text"
            placeholder="Search by name, phone or email…"
            class="pl-8 pr-3 py-1.5 text-sm border border-n-weak rounded-lg bg-n-background text-n-slate-12 outline-none focus:border-woot-500 w-40 lg:w-52 transition-all"
          />
        </div>

        <!-- Filter toggle -->
        <button
          class="relative flex items-center justify-center w-8 h-8 rounded-lg border transition-colors"
          :class="
            showFilters
              ? 'bg-woot-500 border-woot-500 text-white'
              : 'border-n-weak bg-n-background text-n-slate-8 hover:border-woot-400 hover:text-woot-500'
          "
          title="Filters"
          @click="showFilters = !showFilters"
        >
          <span class="i-lucide-sliders-horizontal size-3.5" />
          <span
            v-if="activeFilterCount"
            class="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-ruby-500 text-white text-[9px] font-bold flex items-center justify-center leading-none"
          >
            {{ activeFilterCount }}
          </span>
        </button>

        <!-- Add Lead -->
        <button
          type="button"
          class="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-woot-500 text-white rounded-lg hover:bg-woot-600 transition-colors font-medium"
          @click.prevent.stop="openLeadForm(null)"
        >
          <span class="i-lucide-plus size-4" />
          <span class="hidden sm:inline">Add Lead</span>
        </button>
      </div>
    </div>

    <!-- Filter row -->
    <transition name="slide-down">
      <div
        v-if="showFilters"
        class="flex items-center gap-2 px-4 py-2 bg-n-background border-b border-n-weak shrink-0 flex-wrap gap-y-2"
      >
        <span class="text-xs font-medium text-n-slate-9">Filters:</span>
        <select
          v-model="selectedAgentId"
          class="text-sm border border-n-weak rounded-lg px-3 py-1.5 bg-n-background text-n-slate-12 outline-none focus:border-woot-500"
        >
          <option value="">All Agents</option>
          <option v-for="a in agents" :key="a.id" :value="a.id">
            {{ a.name }}
          </option>
        </select>

        <select
          v-model="selectedChannel"
          class="text-sm border border-n-weak rounded-lg px-3 py-1.5 bg-n-background text-n-slate-12 outline-none focus:border-woot-500"
        >
          <option value="">All Channels</option>
          <option v-for="c in channelOptions" :key="c.value" :value="c.value">
            {{ c.label }}
          </option>
        </select>

        <select
          v-model="selectedPriority"
          class="text-sm border border-n-weak rounded-lg px-3 py-1.5 bg-n-background text-n-slate-12 outline-none focus:border-woot-500"
        >
          <option value="">All Priorities</option>
          <option v-for="p in priorityOptions" :key="p.value" :value="p.value">
            {{ p.label }}
          </option>
        </select>

        <select
          v-model="selectedLeadType"
          class="text-sm border border-n-weak rounded-lg px-3 py-1.5 bg-n-background text-n-slate-12 outline-none focus:border-woot-500"
        >
          <option value="">All Lead Types</option>
          <option value="buyer">Buyers</option>
          <option value="broker">Brokers / CP</option>
        </select>

        <button
          v-if="activeFilterCount"
          class="text-xs text-ruby-600 hover:text-ruby-700 font-medium px-2 py-1.5 rounded-lg hover:bg-ruby-50 transition-colors"
          @click="clearFilters"
        >
          Clear filters
        </button>
      </div>
    </transition>

    <!-- Error banner -->
    <div
      v-if="loadError"
      class="flex items-center gap-2 px-4 py-2 bg-ruby-50 dark:bg-ruby-500/10 border-b border-ruby-200 dark:border-ruby-500/20 text-sm text-ruby-700 dark:text-ruby-400 shrink-0"
    >
      <span class="i-lucide-alert-circle size-4 shrink-0" />
      {{ loadError }}
      <button
        class="underline ml-1 font-medium"
        @click="
          loadError = '';
          loadLeads();
        "
      >
        Retry
      </button>
    </div>

    <!-- Today's site visits widget -->
    <TodaySiteVisitsWidget ref="todayVisitsWidgetRef" />

    <!-- Summary strip -->
    <div
      class="flex items-stretch gap-0 px-4 py-2.5 overflow-x-auto shrink-0 border-b border-n-weak bg-n-background"
    >
      <KanbanSummaryCard
        v-for="stat in summaryStats"
        :key="stat.key"
        :label="stat.label"
        :count="stat.count"
        :today-count="stat.todayCount"
        :icon="stat.icon"
        :color="stat.color"
      />
    </div>

    <!-- Loading -->
    <div
      v-if="uiFlags.isFetching && leads.length === 0"
      class="flex-1 flex items-center justify-center"
    >
      <div class="flex flex-col items-center gap-3 text-n-slate-7">
        <span class="i-lucide-loader-2 size-7 animate-spin text-woot-500" />
        <span class="text-sm">Loading leads…</span>
      </div>
    </div>

    <!-- Kanban board -->
    <div v-else class="flex-1 overflow-x-auto overflow-y-hidden">
      <div
        class="relative flex gap-3 h-full px-4 pt-3 pb-3"
        :class="filteredStages.length > 0 ? 'min-w-max' : 'w-full'"
      >
        <KanbanColumn
          v-for="stage in filteredStages"
          :key="stage.key"
          :stage="stage"
          :leads="leadsForStage(stage.key)"
          @card-click="openDrawer"
          @move-stage="onMoveStage"
          @add-lead="openLeadForm"
        />

        <!-- Filter result empty state -->
        <div
          v-if="
            filteredStages.length > 0 &&
            filteredLeads.length === 0 &&
            (selectedAgentId ||
              selectedChannel ||
              selectedPriority ||
              selectedLeadType ||
              searchQuery.trim())
          "
          class="absolute inset-0 flex flex-col items-center justify-center gap-3 pointer-events-none"
        >
          <span class="i-lucide-search size-6 text-n-slate-6" />
          <p class="text-sm text-n-slate-9 font-medium">
            No leads match your filters.
          </p>
        </div>

        <!-- Empty state -->
        <div
          v-if="filteredStages.length === 0 && !uiFlags.isFetching"
          class="flex flex-col items-center justify-center flex-1 gap-5"
        >
          <div
            class="flex items-center justify-center w-14 h-14 rounded-2xl bg-woot-50 dark:bg-woot-500/10"
          >
            <span class="i-lucide-kanban size-7 text-woot-500" />
          </div>
          <div class="text-center max-w-sm">
            <p class="text-base font-semibold text-n-slate-12">
              No stages configured
            </p>
            <p class="text-sm text-n-slate-9 mt-1">
              Create a project to get started. Stages will be set up
              automatically.
            </p>
            <p class="text-xs text-n-slate-7 mt-2 border-t border-n-weak pt-2">
              Kanban shows leads by <strong>sales stage</strong>. Use
              <strong>Verified Leads</strong> in the sidebar for the full
              qualified lead list.
            </p>
          </div>
          <router-link
            :to="{ name: 'nexceo_projects_new', params: { accountId } }"
            class="flex items-center gap-2 px-4 py-2 rounded-lg bg-woot-500 text-white text-sm font-medium hover:bg-woot-600 transition-colors"
          >
            <span class="i-lucide-plus size-4" />
            Create Project
          </router-link>
        </div>
      </div>
    </div>
  </div>

  <!-- Lead details drawer -->
  <LeadDetailsDrawer
    :lead="selectedLead"
    :is-open="isDrawerOpen"
    :stages="filteredStages"
    :agents="agents"
    @close="closeDrawer"
    @updated="onDrawerUpdated"
  />

  <!-- Lead create/edit form -->
  <LeadForm
    :lead="null"
    :is-open="isLeadFormOpen"
    :default-stage-id="selectedStageId"
    :projects="projects"
    :stages="filteredStages"
    :agents="agents"
    @close="closeLeadForm"
    @saved="onFormSaved"
  />
</template>

<style scoped>
.slide-down-enter-active,
.slide-down-leave-active {
  transition: all 0.15s ease;
  overflow: hidden;
}
.slide-down-enter-from,
.slide-down-leave-to {
  opacity: 0;
  max-height: 0;
}
.slide-down-enter-to,
.slide-down-leave-from {
  opacity: 1;
  max-height: 80px;
}
</style>
